import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {DatePicker} from '@/components/shared/DatePicker'
import {Label} from '@/components/ui/label'
import {Textarea} from '@/components/ui/textarea'
import {Switch} from '@/components/ui/switch'
import {Combobox} from '@/components/ui/combobox'
import type {ComboboxOption} from '@/components/ui/combobox'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Plus, Trash2, ChevronUp, ChevronDown} from 'lucide-react'
import {useAppConfig, type LocalizedTypeValue} from '@/hooks/use-app-config'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import {
    ApprovalConditionField,
    ApprovalPolicyType,
    ConditionOperator,
    ResolverType,
    ApprovalStrategy,
} from '@/types/approval-policy'
import type {
    ApprovalPolicy,
    ApprovalPolicyConditionRequest,
    ApprovalPolicyStepRequest,
    CreateApprovalPolicyRequest,
    UpdateApprovalPolicyRequest,
} from '@/types/approval-policy'
import {CreateApprovalPolicyRequestFields} from '@/types/approval-policy'

interface ApprovalPolicyFormProps {
    policy?: ApprovalPolicy
    onSubmit: (data: CreateApprovalPolicyRequest | UpdateApprovalPolicyRequest) => Promise<void>
    onCancel: () => void
    isLoading?: boolean
}

const ALL_POLICY_TYPES = Object.values(ApprovalPolicyType)
const CONDITION_OPERATORS = Object.values(ConditionOperator)
const STRATEGIES = Object.values(ApprovalStrategy)

/** Condition fields allowed per policy type */
function getConditionFieldsForType(policyType: ApprovalPolicyType): ApprovalConditionField[] {
    switch (policyType) {
        case ApprovalPolicyType.AUTO_ASSIGN_RULE:
            return [ApprovalConditionField.OWNER_PARTY]
        case ApprovalPolicyType.RESTRICTION_RULE:
            return [ApprovalConditionField.OWNER_PARTY]
        case ApprovalPolicyType.FUNCTION_GROUP:
            return [ApprovalConditionField.FUNCTION_GROUP_TYPE, ApprovalConditionField.OWNER_PARTY]
        case ApprovalPolicyType.ACCESS_PROFILE:
        default:
            return [
                ApprovalConditionField.RISK_LEVEL,
                ApprovalConditionField.RESOURCE_TYPE,
                ApprovalConditionField.DATA_CLASSIFICATION,
                ApprovalConditionField.OWNER_PARTY,
            ]
    }
}

/** Resolver types allowed per policy type */
function getResolverTypesForType(policyType: ApprovalPolicyType): ResolverType[] {
    const common: ResolverType[] = [ResolverType.MANAGER, ResolverType.SPECIFIC_IDENTITY, ResolverType.TEAM_MEMBER]
    switch (policyType) {
        case ApprovalPolicyType.AUTO_ASSIGN_RULE:
            return [ResolverType.RULE_OWNER, ...common]
        case ApprovalPolicyType.RESTRICTION_RULE:
            return [ResolverType.RULE_OWNER, ...common]
        case ApprovalPolicyType.FUNCTION_GROUP:
            return [ResolverType.FUNCTION_GROUP_OWNER, ...common]
        case ApprovalPolicyType.ACCESS_PROFILE:
        default:
            return [ResolverType.RESOURCE_OWNER, ResolverType.PROFILE_OWNER, ...common]
    }
}

/** Default step resolver per policy type */
function getDefaultResolverForType(policyType: ApprovalPolicyType): ResolverType {
    switch (policyType) {
        case ApprovalPolicyType.AUTO_ASSIGN_RULE:
            return ResolverType.RULE_OWNER
        case ApprovalPolicyType.RESTRICTION_RULE:
            return ResolverType.RULE_OWNER
        case ApprovalPolicyType.FUNCTION_GROUP:
            return ResolverType.FUNCTION_GROUP_OWNER
        case ApprovalPolicyType.ACCESS_PROFILE:
        default:
            return ResolverType.RESOURCE_OWNER
    }
}

function getValuesForApprovalField(
    field: ApprovalConditionField,
    appConfig: {riskLevelTypes: LocalizedTypeValue[], resourceTypes: LocalizedTypeValue[], dataClassificationTypes: LocalizedTypeValue[], functionGroupTypes: LocalizedTypeValue[]},
): {code: string, label: string}[] {
    switch (field) {
        case ApprovalConditionField.RISK_LEVEL:
            return appConfig.riskLevelTypes.map(tv => ({code: tv.code, label: tv.label}))
        case ApprovalConditionField.RESOURCE_TYPE:
            return appConfig.resourceTypes.map(tv => ({code: tv.code, label: tv.label}))
        case ApprovalConditionField.DATA_CLASSIFICATION:
            return appConfig.dataClassificationTypes.map(tv => ({code: tv.code, label: tv.label}))
        case ApprovalConditionField.FUNCTION_GROUP_TYPE:
            return appConfig.functionGroupTypes.map(tv => ({code: tv.code, label: tv.label}))
        case ApprovalConditionField.OWNER_PARTY:
            return []
    }
}

export function resolverNeedsParam(type: ResolverType): boolean {
    return type === ResolverType.RESOURCE_OWNER
        || type === ResolverType.PROFILE_OWNER
        || type === ResolverType.SPECIFIC_IDENTITY
        || type === ResolverType.TEAM_MEMBER
}

export function ApprovalPolicyForm({policy, onSubmit, onCancel, isLoading}: ApprovalPolicyFormProps) {
    const isEdit = !!policy
    const i18n = useI18n()
    const {toast} = useToast()
    const appConfig = useAppConfig()
    const services = useServices()

    const [name, setName] = useState(policy?.name || '')
    const [description, setDescription] = useState(policy?.description || '')
    const [policyType, setPolicyType] = useState<ApprovalPolicyType>(
        policy?.policyType ?? ApprovalPolicyType.ACCESS_PROFILE
    )
    const [priority, setPriority] = useState<number | ''>(policy?.priority ?? '')
    const [active, setActive] = useState(policy?.active ?? true)
    const [allowProxyApproval, setAllowProxyApproval] = useState(policy?.allowProxyApproval ?? false)
    const [validFrom, setValidFrom] = useState(policy?.validFrom ?? '')
    const [validTo, setValidTo] = useState(policy?.validTo ?? '')

    const [conditions, setConditions] = useState<ApprovalPolicyConditionRequest[]>(
        policy?.conditions.map(c => ({field: c.field, operator: c.operator, value: c.value})) || []
    )

    const [steps, setSteps] = useState<ApprovalPolicyStepRequest[]>(
        policy?.steps.map(s => ({
            stepOrder: s.stepOrder,
            resolverType: s.resolverType,
            resolverParam: s.resolverParam || undefined,
            approvalStrategy: s.approvalStrategy,
            required: s.required,
        })) || [{stepOrder: 1, resolverType: ResolverType.RESOURCE_OWNER, resolverParam: 'APPLICATION_OWNER', approvalStrategy: ApprovalStrategy.ANY, required: true}]
    )

    const [teamSearchQuery, setTeamSearchQuery] = useState('')

    const {data: teamSearchData, isLoading: teamSearchLoading} = useQuery({
        queryKey: ['parties', 'team-search', teamSearchQuery],
        queryFn: () => services.parties.list({q: teamSearchQuery, partyType: 'TEAM', size: 10}),

    })

    const teamOptions: ComboboxOption[] = (teamSearchData?.content || []).map((t) => ({
        value: t.id,
        label: t.derivedDisplayName,
    }))

    const teamMemberStepParam = steps.find(s => s.resolverType === ResolverType.TEAM_MEMBER)?.resolverParam

    const {data: selectedTeam} = useQuery({
        queryKey: ['parties', teamMemberStepParam],
        queryFn: () => services.parties.detail(teamMemberStepParam!),
        enabled: !!teamMemberStepParam,
    })

    /** Handle policy type change: clear incompatible conditions and steps, show toast */
    const handlePolicyTypeChange = (newType: ApprovalPolicyType) => {
        if (newType === policyType) return
        const allowedFields = getConditionFieldsForType(newType)
        const allowedResolvers = getResolverTypesForType(newType)

        const compatibleConditions = conditions.filter(c => allowedFields.includes(c.field))
        const compatibleSteps = steps.filter(s => allowedResolvers.includes(s.resolverType))

        const conditionsRemoved = conditions.length - compatibleConditions.length
        const stepsRemoved = steps.length - compatibleSteps.length

        if (conditionsRemoved > 0 || stepsRemoved > 0) {
            toast({description: i18n.approvalPolicies.incompatibleConditionsRemoved})
        }

        setConditions(compatibleConditions)

        if (compatibleSteps.length === 0) {
            // Add a default step for the new type
            const defaultResolver = getDefaultResolverForType(newType)
            const defaultParam = defaultResolver === ResolverType.RESOURCE_OWNER ? i18n.approvalPolicies.defaultResolverParam : undefined
            setSteps([{
                stepOrder: 1,
                resolverType: defaultResolver,
                resolverParam: defaultParam,
                approvalStrategy: ApprovalStrategy.ANY,
                required: true,
            }])
        } else {
            setSteps(renumberSteps(compatibleSteps))
        }

        setPolicyType(newType)
    }

    const addCondition = () => {
        const allowedFields = getConditionFieldsForType(policyType)
        const defaultField = allowedFields[0] ?? ApprovalConditionField.RISK_LEVEL
        setConditions([...conditions, {field: defaultField, operator: ConditionOperator.EQUALS, value: ''}])
    }

    const removeCondition = (index: number) => {
        setConditions(conditions.filter((_, i) => i !== index))
    }

    const updateCondition = (index: number, updates: Partial<ApprovalPolicyConditionRequest>) => {
        setConditions(conditions.map((c, i) => {
            if (i !== index) return c
            // Reset value when field changes (options differ per field)
            if (updates.field && updates.field !== c.field) {
                return {...c, ...updates, value: ''}
            }
            return {...c, ...updates}
        }))
    }

    const toggleInValue = (index: number, code: string) => {
        setConditions(conditions.map((c, i) => {
            if (i !== index) return c
            const current = c.value ? c.value.split(',') : []
            const next = current.includes(code)
                ? current.filter(v => v !== code)
                : [...current, code]
            return {...c, value: next.join(',')}
        }))
    }

    const addStep = () => {
        setSteps(prev => {
            const nextOrder = prev.length > 0 ? Math.max(...prev.map(s => s.stepOrder)) + 1 : 1
            const defaultResolver = getDefaultResolverForType(policyType)
            const defaultParam = defaultResolver === ResolverType.RESOURCE_OWNER
                ? i18n.approvalPolicies.defaultResolverParam
                : undefined
            return [...prev, {
                stepOrder: nextOrder,
                resolverType: defaultResolver,
                resolverParam: defaultParam,
                approvalStrategy: ApprovalStrategy.ANY,
                required: true,
            }]
        })
    }

    const removeStep = (index: number) => {
        setSteps(prev => renumberSteps(prev.filter((_, i) => i !== index)))
    }

    const updateStep = (index: number, updates: Partial<ApprovalPolicyStepRequest>) => {
        setSteps(prev => prev.map((s, i) => {
            if (i !== index) return s
            const merged = {...s, ...updates}
            if (updates.resolverType && updates.resolverType !== s.resolverType) {
                merged.resolverParam = undefined
            }
            return merged
        }))
    }

    const moveStep = (index: number, direction: -1 | 1) => {
        setSteps(prev => {
            const newIndex = index + direction
            if (newIndex < 0 || newIndex >= prev.length) return prev
            const updated = [...prev]
            const temp = updated[index]
            updated[index] = updated[newIndex]
            updated[newIndex] = temp
            return renumberSteps(updated)
        })
    }

    const renumberSteps = (stepList: ApprovalPolicyStepRequest[]) =>
        stepList.map((s, i) => ({...s, stepOrder: i + 1}))

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const base = {
            name,
            description: description || undefined,
            policyType,
            priority: Number(priority),
            active,
            allowProxyApproval,
            validFrom: validFrom || undefined,
            validTo: validTo || undefined,
            conditions,
            steps: renumberSteps(steps),
        }

        if (isEdit) {
            await onSubmit({...base, version: policy.version} as UpdateApprovalPolicyRequest)
        } else {
            await onSubmit(base as CreateApprovalPolicyRequest)
        }
    }

    const allowedConditionFields = getConditionFieldsForType(policyType)
    const allowedResolverTypes = getResolverTypesForType(policyType)

    const isValid = name && priority !== '' && Number(priority) >= 0 && steps.length > 0
        && steps.every(s => !resolverNeedsParam(s.resolverType) || s.resolverParam)
        && conditions.every(c => c.value || c.field === ApprovalConditionField.OWNER_PARTY)

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
                <Label htmlFor={CreateApprovalPolicyRequestFields.name}>{i18n.approvalPolicies.name} *</Label>
                <Input
                    id={CreateApprovalPolicyRequestFields.name}
                    data-testid="input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={i18n.approvalPolicies.namePlaceholder}
                    required
                />
            </div>

            {/* Policy Type */}
            <div className="space-y-2">
                <Label htmlFor={CreateApprovalPolicyRequestFields.policyType}>{i18n.approvalPolicies.policyType} *</Label>
                {isEdit ? (
                    <div className="flex items-center gap-2 h-9" data-testid="policy-type-readonly">
                        <Badge variant="outline">{i18n.approvalPolicies.policyTypeLabels[policyType]}</Badge>
                        <span className="text-xs text-muted-foreground">({i18n.approvalPolicies.policyType})</span>
                    </div>
                ) : (
                    <Select
                        value={policyType}
                        onValueChange={(v) => handlePolicyTypeChange(v as ApprovalPolicyType)}
                    >
                        <SelectTrigger id={CreateApprovalPolicyRequestFields.policyType} data-testid="select-policy-type">
                            <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                            {ALL_POLICY_TYPES.map((pt) => (
                                <SelectItem key={pt} value={pt} data-testid={`option-${pt}`}>
                                    {i18n.approvalPolicies.policyTypeLabels[pt]}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Description */}
            <div className="space-y-2">
                <Label htmlFor={CreateApprovalPolicyRequestFields.description}>{i18n.approvalPolicies.description}</Label>
                <Textarea
                    id={CreateApprovalPolicyRequestFields.description}
                    data-testid="input-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={i18n.approvalPolicies.descriptionPlaceholder}
                    rows={2}
                />
            </div>

            {/* Priority */}
            <div className="space-y-2">
                <Label htmlFor={CreateApprovalPolicyRequestFields.priority}>{i18n.approvalPolicies.priority} *</Label>
                <Input
                    id={CreateApprovalPolicyRequestFields.priority}
                    data-testid="input-priority"
                    type="number"
                    min="0"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value ? Number(e.target.value) : '')}
                    placeholder={i18n.approvalPolicies.priorityPlaceholder}
                    required
                />
                <p className="text-xs text-muted-foreground">{i18n.approvalPolicies.priorityHelp}</p>
            </div>

            {/* Active switch */}
            <div className="flex items-center justify-between">
                <div>
                    <Label htmlFor={CreateApprovalPolicyRequestFields.active}>{i18n.approvalPolicies.active}</Label>
                    <p className="text-sm text-muted-foreground">{i18n.approvalPolicies.activeDesc}</p>
                </div>
                <Switch id={CreateApprovalPolicyRequestFields.active} checked={active} onCheckedChange={setActive}/>
            </div>

            {/* Allow proxy approval switch */}
            {/* eslint-disable i18next/no-literal-string -- pending translation */}
            <div className="flex items-center justify-between">
                <div>
                    <Label htmlFor={CreateApprovalPolicyRequestFields.allowProxyApproval}>Allow proxy approval</Label>
                    <p className="text-sm text-muted-foreground">
                        When ON, the requester may also approve as long as they are not the beneficiary.
                        The beneficiary is always excluded from approving (segregation of duties).
                    </p>
                </div>
                <Switch id={CreateApprovalPolicyRequestFields.allowProxyApproval}
                        checked={allowProxyApproval} onCheckedChange={setAllowProxyApproval}/>
            </div>
            {/* eslint-enable i18next/no-literal-string */}

            {/* Valid From / To */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor={CreateApprovalPolicyRequestFields.validFrom}>{i18n.approvalPolicies.validFrom}</Label>
                    <DatePicker
                        testId={CreateApprovalPolicyRequestFields.validFrom}
                        withTime
                        value={validFrom}
                        onChange={setValidFrom}
                        ariaLabel={String(i18n.approvalPolicies.validFrom)}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor={CreateApprovalPolicyRequestFields.validTo}>{i18n.approvalPolicies.validTo}</Label>
                    <DatePicker
                        testId={CreateApprovalPolicyRequestFields.validTo}
                        withTime
                        value={validTo}
                        onChange={setValidTo}
                        ariaLabel={String(i18n.approvalPolicies.validTo)}
                    />
                </div>
            </div>

            {/* Conditions */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                        <CardTitle className="text-base" data-testid="card-title-conditions">{i18n.approvalPolicies.conditions}</CardTitle>
                        <CardDescription>{i18n.approvalPolicies.conditionsDesc}</CardDescription>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addCondition} data-testid="btn-add-condition">
                        <Plus className="h-4 w-4 mr-1"/>
                        {i18n.approvalPolicies.addCondition}
                    </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                    {conditions.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                            {i18n.approvalPolicies.noConditions}
                        </p>
                    ) : conditions.map((condition, index) => (
                        <div key={index} className="flex items-end gap-2">
                            <div className="flex-1">
                                {index === 0 && <Label className="text-xs">{i18n.approvalPolicies.field}</Label>}
                                <Select
                                    value={condition.field}
                                    onValueChange={(v) => updateCondition(index, {field: v as ApprovalConditionField})}
                                >
                                    <SelectTrigger data-testid="select-condition-field">
                                        <SelectValue/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {allowedConditionFields.map((f) => (
                                            <SelectItem key={f} value={f}>
                                                {i18n.approvalPolicies.fieldLabels[f]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {condition.field !== ApprovalConditionField.OWNER_PARTY && (
                                <>
                                    <div className="w-40">
                                        {index === 0 && <Label className="text-xs">{i18n.approvalPolicies.operator}</Label>}
                                        <Select
                                            value={condition.operator}
                                            onValueChange={(v) => updateCondition(index, {operator: v as ConditionOperator})}
                                        >
                                            <SelectTrigger data-testid="select-condition-operator">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CONDITION_OPERATORS.map((op) => (
                                                    <SelectItem key={op} value={op}>
                                                        {i18n.approvalPolicies.operatorLabels[op]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex-1">
                                        {index === 0 && <Label className="text-xs">{i18n.approvalPolicies.value}</Label>}
                                        {condition.operator === ConditionOperator.IN ? (
                                            <div className="flex flex-wrap gap-1 min-h-[36px] items-center" data-testid={`badges-condition-value-${index}`}>
                                                {getValuesForApprovalField(condition.field, appConfig).map((opt) => {
                                                    const selected = condition.value.split(',').includes(opt.code)
                                                    return (
                                                        <Badge
                                                            key={opt.code}
                                                            variant={selected ? 'default' : 'outline'}
                                                            className="cursor-pointer select-none"
                                                            onClick={() => toggleInValue(index, opt.code)}
                                                            data-testid={`badge-${opt.code}`}
                                                        >
                                                            {opt.label}
                                                        </Badge>
                                                    )
                                                })}
                                            </div>
                                        ) : (
                                            <Select
                                                value={condition.value}
                                                onValueChange={(v) => updateCondition(index, {value: v})}
                                            >
                                                <SelectTrigger data-testid={`select-condition-value-${index}`}>
                                                    <SelectValue placeholder={i18n.approvalPolicies.conditionValuePlaceholder}/>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {getValuesForApprovalField(condition.field, appConfig).map((opt) => (
                                                        <SelectItem key={opt.code} value={opt.code}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </div>
                                </>
                            )}
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeCondition(index)}
                                className="text-destructive hover:text-destructive shrink-0"
                                aria-label={i18n.approvalPolicies.removeCondition}
                                data-testid={`btn-remove-condition-${index}`}
                            >
                                <Trash2 className="h-4 w-4"/>
                            </Button>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Steps */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                        <CardTitle className="text-base" data-testid="card-title-steps">{i18n.approvalPolicies.steps}</CardTitle>
                        <CardDescription>{i18n.approvalPolicies.stepsDesc}</CardDescription>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addStep} data-testid="btn-add-step">
                        <Plus className="h-4 w-4 mr-1"/>
                        {i18n.approvalPolicies.addStep}
                    </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                    {steps.length === 0 ? (
                        <p className="text-sm text-destructive text-center py-4">
                            {i18n.approvalPolicies.atLeastOneStep}
                        </p>
                    ) : steps.map((step, index) => (
                        <div key={index} className="flex items-end gap-2 p-3 border rounded-lg">
                            <div className="flex flex-col gap-1 shrink-0">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => moveStep(index, -1)}
                                    disabled={index === 0}
                                    aria-label={i18n.approvalPolicies.moveUp}
                                    data-testid={`btn-move-step-up-${index}`}
                                >
                                    <ChevronUp className="h-3 w-3"/>
                                </Button>
                                <span className="text-xs text-center text-muted-foreground font-mono">{step.stepOrder}</span>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => moveStep(index, 1)}
                                    disabled={index === steps.length - 1}
                                    aria-label={i18n.approvalPolicies.moveDown}
                                    data-testid={`btn-move-step-down-${index}`}
                                >
                                    <ChevronDown className="h-3 w-3"/>
                                </Button>
                            </div>
                            <div className="flex-1 space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-xs">{i18n.approvalPolicies.resolverType}</Label>
                                        <Select
                                            value={step.resolverType}
                                            onValueChange={(v) => updateStep(index, {resolverType: v as ResolverType})}
                                        >
                                            <SelectTrigger data-testid="select-resolver-type">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {allowedResolverTypes.map((rt) => (
                                                    <SelectItem key={rt} value={rt} data-testid={`option-${rt}`}>
                                                        {i18n.approvalPolicies.resolverTypeLabels[rt]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs">{i18n.approvalPolicies.approvalStrategy}</Label>
                                        <Select
                                            value={step.approvalStrategy}
                                            onValueChange={(v) => updateStep(index, {approvalStrategy: v as ApprovalStrategy})}
                                        >
                                            <SelectTrigger data-testid="select-approval-strategy">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STRATEGIES.map((s) => (
                                                    <SelectItem key={s} value={s}>
                                                        {i18n.approvalPolicies.strategyLabels[s]}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {resolverNeedsParam(step.resolverType) && (
                                    <div>
                                        <Label className="text-xs">{i18n.approvalPolicies.resolverParam}</Label>
                                        {step.resolverType === ResolverType.TEAM_MEMBER ? (
                                            <div data-testid={`step-team-picker-${index}`}>
                                                <Combobox
                                                    options={teamOptions}
                                                    value={step.resolverParam || null}
                                                    onValueChange={(v) => updateStep(index, {resolverParam: v || undefined})}
                                                    onSearch={setTeamSearchQuery}
                                                    placeholder={i18n.approvalPolicies.resolverParamPlaceholder.TEAM_MEMBER}
                                                    isLoading={teamSearchLoading}
                                                    selectedLabel={selectedTeam?.derivedDisplayName}
                                                />
                                            </div>
                                        ) : (
                                            <Input
                                                value={step.resolverParam || ''}
                                                onChange={(e) => updateStep(index, {resolverParam: e.target.value || undefined})}
                                                placeholder={i18n.approvalPolicies.resolverParamPlaceholder[step.resolverType as keyof typeof i18n.approvalPolicies.resolverParamPlaceholder] || ''}
                                                required
                                            />
                                        )}
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={step.required}
                                        onCheckedChange={(v) => updateStep(index, {required: v})}
                                    />
                                    <Label className="text-xs">{i18n.approvalPolicies.required}</Label>
                                    <span className="text-xs text-muted-foreground">{i18n.approvalPolicies.requiredDesc}</span>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => removeStep(index)}
                                className="text-destructive hover:text-destructive shrink-0"
                                disabled={steps.length <= 1}
                                aria-label={i18n.approvalPolicies.removeStep}
                                data-testid={`btn-remove-step-${index}`}
                            >
                                <Trash2 className="h-4 w-4"/>
                            </Button>
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading} data-testid="btn-cancel">
                    {i18n.common.cancel}
                </Button>
                <Button type="submit" disabled={isLoading || !isValid} data-testid="btn-submit">
                    {isLoading ? i18n.common.saving : (isEdit ? i18n.common.saveChanges : i18n.approvalPolicies.createPolicy)}
                </Button>
            </div>
        </form>
    )
}
